import { useCallback, useEffect, useState } from 'react'
import { getGrowthGoal, setGrowthGoalTarget, type GrowthGoal } from '../lib/adminClient'
import { useAuth } from '../lib/auth'
import { day, num } from '../lib/format'
import { DrillValue } from './Drill'
import { LoadError } from './LoadError'

// The goal the owner set on 2026-10-31: the next 100 paying subscribers.
// Read from GET /admin/growth/goal (server: adminGrowth.ts). Progress is net:
// paying now minus paying when the goal was set, so someone who stops paying
// takes one back off. Every number opens the accounts it was counted from.

export function GoalTile(): JSX.Element {
  const session = useAuth()
  const [goal, setGoal] = useState<GrowthGoal | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [editing, setEditing] = useState(false)
  const [target, setTarget] = useState('')
  const [saveError, setSaveError] = useState<string | null>(null)

  const load = useCallback(async () => {
    const res = await getGrowthGoal()
    if (res.ok) {
      setGoal(res.data.goal)
      setError(null)
    } else setError(res.error)
  }, [])
  useEffect(() => void load(), [load])

  async function save(): Promise<void> {
    setSaveError(null)
    const res = await setGrowthGoalTarget(Number(target))
    if (!res.ok) return setSaveError(res.error)
    setGoal(res.data.goal)
    setEditing(false)
  }

  if (error) return <LoadError what="the subscriber goal" endpoint="GET /admin/growth/goal" error={error} testId="goal-error" />
  if (!goal) return <div className="glass-card rounded-xl p-5 mb-4 text-sm text-slate-400">Loading the goal…</div>

  const gain = goal.target - goal.baseline
  const progress = gain > 0 ? Math.max(0, Math.min(1, goal.net / gain)) : 0
  const canEdit = session?.role === 'owner' || session?.role === 'admin'
  return (
    <div className="glass-card rounded-xl p-5 mb-4" data-testid="goal-tile">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.16em] text-slate-400">Goal: the next {num(gain)} paying subscribers</div>
          <div className="text-3xl font-semibold mt-2 tabular-nums" data-testid="goal-net">
            {goal.net >= 0 ? '+' : ''}
            {num(goal.net)} <span className="text-base text-slate-400 font-normal">of {num(gain)}</span>
          </div>
          <div className="text-xs text-slate-400 mt-1">
            <DrillValue to={goal.payingNow.ref} label="Paying subscribers now" expect={{ key: 'count', value: goal.payingNow.count }} testId="goal-paying">
              {num(goal.payingNow.count)} paying now
            </DrillValue>{' '}
            · {num(goal.baseline)} when counting started on {day(goal.startedAt)} · target {num(goal.target)}
          </div>
          <div className="text-xs text-slate-400 mt-0.5">
            <DrillValue to={goal.newSince.ref} label="New paying subscribers since the goal was set" expect={{ key: 'count', value: goal.newSince.count }} testId="goal-new">
              {num(goal.newSince.count)} new
            </DrillValue>{' '}
            since then{goal.newSince.count > goal.net ? `; ${num(goal.newSince.count - goal.net)} fewer net because some stopped paying` : ''}.
          </div>
        </div>
        <div className="min-w-[16rem]">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5">Where the paying subscribers came from</div>
          <ul className="space-y-1 text-sm" data-testid="goal-sources">
            {goal.bySource.map((s) => (
              <li key={s.key} className="flex justify-between gap-4">
                <span className="text-slate-300">{s.label}</span>
                <DrillValue to={s.ref} label={`Paying subscribers: ${s.label}`} expect={{ key: 'count', value: s.count }} testId={`goal-source-${s.key}`}>
                  <span className="tabular-nums">{num(s.count)}</span>
                </DrillValue>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className="h-2 rounded bg-white/5 overflow-hidden mt-4" aria-label={`${Math.round(progress * 100)}% of the goal`}>
        <div className="h-full bg-accent/70" style={{ width: `${progress * 100}%` }} />
      </div>
      <div className="flex flex-wrap items-center gap-3 mt-3 text-xs text-slate-500">
        <span>Paying means a live Stripe subscription on an active account; referral credit and granted plans are not counted.</span>
        {canEdit && !editing && (
          <button className="btn-ghost ml-auto" onClick={() => { setTarget(String(goal.target)); setEditing(true) }} data-testid="goal-edit">
            Change target
          </button>
        )}
        {editing && (
          <span className="ml-auto flex items-center gap-2">
            <input
              type="number"
              min={1}
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              className="w-24 bg-black/30 border border-white/10 rounded-md px-2 py-1 text-sm"
              data-testid="goal-target-input"
            />
            <button className="btn-primary" onClick={() => void save()} data-testid="goal-save">Save</button>
            <button className="btn-ghost" onClick={() => setEditing(false)}>Cancel</button>
          </span>
        )}
        {saveError && <span className="w-full text-red-300">{saveError}</span>}
      </div>
    </div>
  )
}
